import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = ["src"]
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = {
  key: 0,
  class: "flex flex-col items-start justify-center text-white z-[1]"
}
const _hoisted_6 = { class: "text-[10px] font-body text-shadow-1xl" }
const _hoisted_7 = { class: "text-base text-shadow-1xl mb-[6px]" }
const _hoisted_8 = { class: "uppercase text-m mb-4 text-shadow-1xl" }
const _hoisted_9 = { class: "font-body text-[10px] text-shadow-1xl" }
const _hoisted_10 = {
  key: 1,
  class: "flex flex-col space-y-2 z-[1] p-1"
}
const _hoisted_11 = ["src"]
const _hoisted_12 = {
  key: 2,
  class: "absolute inset-0 flex flex-col items-center pb-4 px-4 z-[1]"
}
const _hoisted_13 = { class: "bg-white w-full p-1 flex justify-center items-center rounded-md h-[100%]" }
const _hoisted_14 = ["src", "alt"]

import { computed } from 'vue';
import BaseBarcode from '@/components/BarCode/BaseBarcode.vue';
import defaultCompanyLogo from '@/assets/racq-logo.svg';
import defaultThumbPlaceholder from '@/assets/card-skeleton.svg';
import blue from '@/assets/loyalty-logos/Blue.svg';
import bronze from '@/assets/loyalty-logos/Bronze.svg';
import silver from '@/assets/loyalty-logos/Silver.svg';
import gold from '@/assets/loyalty-logos/Gold.svg';
import gold50 from '@/assets/loyalty-logos/Gold50.svg';
import smileBlue from '@/assets/card-stripes/smile-blue.png';
import smileBronze from '@/assets/card-stripes/smile-bronze.png';
import smileSilver from '@/assets/card-stripes/smile-silver.png';
import smileGold from '@/assets/card-stripes/smile-gold.png';
import smileGold50 from '@/assets/card-stripes/smile-gold50.png';
import { useAccountStore } from '@/store';
import { toFormattedCardNumber } from '@/utils/convert';
import { format as formatDate } from 'date-fns';
import { CardVariant } from '@/enums/membership-card-variant.enum';
import { LoyaltyLevels } from '@/enums/loyalty-levels.enum';
import { hasLoyaltyLevel } from '@/utils/loyalty-level';

interface Props {
  /** visual variant */
  variant?: CardVariant;
  level?: string;
  /** copy */
  cardTitle?: string; // e.g., "MEMBERSHIP CARD No."
  joinDateLabel?: string; // e.g., "JOIN DATE"
  /** member data */
  displayMemberNumber?: string;
  extraClasses?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MembershipCard',
  props: {
    variant: { default: CardVariant.FRONT },
    level: { default: undefined },
    cardTitle: { default: 'MEMBERSHIP CARD No.' },
    joinDateLabel: { default: 'JOIN DATE' },
    displayMemberNumber: { default: '' },
    extraClasses: { default: '' }
  },
  setup(__props: any) {

const accountStore = useAccountStore();

const props = __props;

const defaultLevelLogos: Record<string, string> = {
  '':'',
  blue,
  bronze,
  silver,
  gold,
  gold50,
};
const defaultSmileStripes: Record<string, string> = {
  ['']: '',
  [LoyaltyLevels.Blue]: smileBlue,
  [LoyaltyLevels.Bronze]: smileBronze,
  [LoyaltyLevels.Silver]: smileSilver,
  [LoyaltyLevels.Gold]: smileGold,
  [LoyaltyLevels.Gold50]: smileGold50,
};

const headerPaddingClass = computed(() =>
  props.variant === 'thumbnail' ? 'p-1' : props.variant === 'front' ? 'px-4 py-3' : 'px-4 py-5'
);

const backgroundClassMap: Record<string, string> = {
  '':'',
  [LoyaltyLevels.Blue]: 'bg-gradient-blue-tier',
  [LoyaltyLevels.Bronze]: 'bg-gradient-bronze-tier',
  [LoyaltyLevels.Silver]: 'bg-gradient-silver-tier',
  [LoyaltyLevels.Gold]: 'bg-gradient-gold-tier',
  [LoyaltyLevels.Gold50]: 'bg-gradient-gold-highlight',
};

const backgroundClass = computed(() => backgroundClassMap[memberLoyaltyLevel.value]);

const variantClass = computed(() => {
  switch (props.variant) {
    case 'front':
      return 'w-[260px] h-[162px] rounded-xl shadow-2xl';
    case 'thumbnail':
      return 'w-[86px] h-[53.5px] rounded-md shadow-1xl';
    case 'back':
      return 'w-[auto] aspect-racq-card rounded-xl shadow-1xl';
    default:
      return 'w-[260px] h-[162px] rounded-xl shadow-1xl';
  }
});

const levelLogoSrc = computed(
  () => defaultLevelLogos[memberLoyaltyLevel.value] ?? defaultLevelLogos[LoyaltyLevels.None]
);
const smileStripeSrc = computed(
  () => defaultSmileStripes[memberLoyaltyLevel.value] ?? defaultLevelLogos[LoyaltyLevels.None]
);

const memberCardNumber = computed(() =>
  toFormattedCardNumber(accountStore.identityDetails?.membershipCardNumber || '')
);
const memberLoyaltyLevel = computed(
  () => props.level || accountStore.identityDetails?.membershipLoyalty?.toLowerCase() || LoyaltyLevels.None
);
const memberJoinedDate = computed(() =>
  accountStore.identityDetails && accountStore.identityDetails.membershipJoined
    ? formatDate(accountStore.identityDetails.membershipJoined, 'yyyy')
    : ''
);
const memberName = computed(() => {
  return `${accountStore.identityDetails?.firstName} ${accountStore.identityDetails?.lastName ?? ''}`;
});
const showMembershipCard = computed(() => hasLoyaltyLevel(memberLoyaltyLevel.value));

return (_ctx: any,_cache: any) => {
  return (showMembershipCard.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass([
      'flex flex-col justify-between overflow-hidden relative max-h-[256px]',
      variantClass.value,
      backgroundClass.value,
      _ctx.extraClasses,
    ]),
        "data-test-id": "member_card"
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["flex justify-between items-center", headerPaddingClass.value])
        }, [
          (_ctx.variant === 'thumbnail')
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: _unref(defaultCompanyLogo),
                alt: "RACQ",
                class: "w-[26px] h-[7px]"
              }, null, 8, _hoisted_1))
            : (_openBlock(), _createElementBlock("img", {
                key: 1,
                src: _unref(defaultCompanyLogo),
                alt: "RACQ",
                class: "w-[79.6px] h-[20.2px]"
              }, null, 8, _hoisted_2)),
          (_ctx.variant === 'thumbnail')
            ? (_openBlock(), _createElementBlock("img", {
                key: 2,
                src: levelLogoSrc.value,
                alt: `${memberLoyaltyLevel.value} logo`,
                class: "w-[10px] h-[4px]"
              }, null, 8, _hoisted_3))
            : (_openBlock(), _createElementBlock("img", {
                key: 3,
                src: levelLogoSrc.value,
                alt: `${memberLoyaltyLevel.value} logo`
              }, null, 8, _hoisted_4))
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["flex-1 flex relative z-[1]", _ctx.variant === 'thumbnail' ? '' : 'px-4'])
        }, [
          (_ctx.variant === 'front')
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", null, [
                  _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.cardTitle), 1),
                  _createElementVNode("p", _hoisted_7, _toDisplayString(memberCardNumber.value), 1)
                ]),
                _createElementVNode("p", _hoisted_8, _toDisplayString(memberName.value), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.joinDateLabel) + ": " + _toDisplayString(memberJoinedDate.value), 1)
              ]))
            : (_ctx.variant === 'thumbnail')
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createElementVNode("img", {
                    src: _unref(defaultThumbPlaceholder),
                    alt: "{{ thumbnailAlt }}",
                    class: "max-w-full max-h-full"
                  }, null, 8, _hoisted_11)
                ]))
              : (_ctx.variant === 'back')
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createVNode(BaseBarcode, {
                        membershipNumber: memberCardNumber.value,
                        class: "max-w-full max-h-full"
                      }, null, 8, ["membershipNumber"])
                    ])
                  ]))
                : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass(["flex absolute w-[100%] h-[100%] bottom-0 z-[0]", _ctx.variant === 'thumbnail' ? 'right-[2.5rem]' : 'right-0'])
          }, [
            _createElementVNode("img", {
              src: smileStripeSrc.value,
              alt: `${memberLoyaltyLevel.value} smile stripe`
            }, null, 8, _hoisted_14)
          ], 2)
        ], 2)
      ], 2))
    : _createCommentVNode("", true)
}
}

})